/**
 * 화면 배율(계정별 확대/축소) — 티켓 #57
 *
 * 시력에 맞춰 화면을 통째로 키우는 장치. 글자만 키우는 방식은 `text-[NNpx]` 고정값이
 * src/ 전반에 깔려 있어 못 쓰고, 대신 CSS `zoom` 을 문서 루트(<html>)에 걸어 글자·버튼·표가
 * 같은 비율로 커지게 한다(브라우저 확대와 같은 느낌).
 *
 * - `<html>` 에 거는 이유: 모달·드롭다운·토스트가 `document.body` 로 포털되어 나가므로
 *   `#root` 에만 걸면 그것들만 원래 크기로 남는다. 루트에 걸면 전부 함께 스케일된다.
 *   이 페이지 안에서만 적용되므로 같은 브라우저의 다른 사이트·탭에는 영향이 없다.
 * - 저장은 **계정별**(서버 ui-prefs `app-zoom`) — 다른 컴퓨터에서 로그인해도 따라온다.
 *   localStorage 는 "직전 값 즉시 반영"용 캐시일 뿐이고, 계정 id 를 함께 저장해
 *   다른 계정으로 로그인하면 캐시를 쓰지 않는다(계정끼리 섞이지 않음).
 * - 로그아웃 상태(로그인 화면·고객 공개 폼)에서는 항상 100% 다.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "./AuthContext";
import { fetchUiPref, saveUiPref } from "../api/uiPrefs";

/** 서버 ui-prefs 키 — 화면 하나가 아니라 앱 전체 설정이라 화면명 대신 app- 접두사. */
const PREF_KEY = "app-zoom";
/** 직전 값 즉시 반영용 로컬 캐시(계정 id 를 함께 저장해 계정 간 혼선 방지). */
const CACHE_KEY = "pb.uiZoom";

/** 단계형 조절 — 70%~130%, 5% 간격. 인덱스가 곧 단계다. */
export const ZOOM_STEPS = [0.7, 0.75, 0.8, 0.85, 0.9, 0.95, 1, 1.05, 1.1, 1.15, 1.2, 1.25, 1.3] as const;
export const DEFAULT_ZOOM = 1;

function nearestStep(value: number): number {
  let best = DEFAULT_ZOOM;
  let bestGap = Number.POSITIVE_INFINITY;
  for (const step of ZOOM_STEPS) {
    const gap = Math.abs(step - value);
    if (gap < bestGap) {
      bestGap = gap;
      best = step;
    }
  }
  return best;
}

/** 서버·캐시에서 온 값은 무엇이 오든 유효 단계로 접는다. */
function normalize(raw: unknown): number {
  if (typeof raw !== "number" || !Number.isFinite(raw)) return DEFAULT_ZOOM;
  return nearestStep(Math.min(1.3, Math.max(0.7, raw)));
}

function applyToDocument(zoom: number) {
  const root = document.documentElement;
  // 100% 는 속성 자체를 지운다 — 기본 상태에서 zoom 이 남아 있지 않게.
  if (zoom === DEFAULT_ZOOM) root.style.removeProperty("zoom");
  else root.style.setProperty("zoom", String(zoom));
}

function readCache(userId: string): number | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { userId?: string; zoom?: number };
    if (parsed?.userId !== userId) return null; // 다른 계정 캐시는 무시
    return normalize(parsed.zoom);
  } catch {
    return null;
  }
}

function writeCache(userId: string, zoom: number) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ userId, zoom }));
  } catch {
    /* 저장 실패(사생활 보호 모드 등)는 무시 — 서버가 진본이라 기능은 그대로 */
  }
}

interface UiZoomContextValue {
  /** 현재 배율(1 = 100%). */
  zoom: number;
  /** 화면 표시용 정수 퍼센트. */
  zoomPercent: number;
  zoomIn: () => void;
  zoomOut: () => void;
  /** 100% 로 되돌리기. */
  resetZoom: () => void;
  canZoomIn: boolean;
  canZoomOut: boolean;
}

const UiZoomContext = createContext<UiZoomContextValue>({
  zoom: DEFAULT_ZOOM,
  zoomPercent: 100,
  zoomIn: () => {},
  zoomOut: () => {},
  resetZoom: () => {},
  canZoomIn: false,
  canZoomOut: false,
});

export function UiZoomProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [zoom, setZoom] = useState<number>(DEFAULT_ZOOM);

  // 서버에서 막 읽어온 값을 그대로 다시 저장하지 않도록(불필요한 PUT 방지) 마지막 저장값 기억.
  const savedRef = useRef<number>(DEFAULT_ZOOM);

  // 계정이 바뀔 때마다: 캐시로 즉시 반영 → 서버 값으로 확정.
  useEffect(() => {
    if (!userId) {
      // 로그아웃/공개 화면 — 항상 100%
      savedRef.current = DEFAULT_ZOOM;
      setZoom(DEFAULT_ZOOM);
      applyToDocument(DEFAULT_ZOOM);
      return;
    }

    const cached = readCache(userId);
    const initial = cached ?? DEFAULT_ZOOM;
    savedRef.current = initial;
    setZoom(initial);
    applyToDocument(initial);

    let alive = true;
    fetchUiPref<{ zoom?: number }>(PREF_KEY)
      .then((cfg) => {
        if (!alive) return;
        const next = cfg == null ? DEFAULT_ZOOM : normalize(cfg.zoom);
        savedRef.current = next;
        setZoom(next);
        applyToDocument(next);
        writeCache(userId, next);
      })
      .catch(() => {
        /* 네트워크 실패 시 캐시값 유지 — 화면이 갑자기 튀지 않게 */
      });
    return () => {
      alive = false;
    };
  }, [userId]);

  // 언마운트 시 원래대로(다른 앱 화면에 zoom 이 남지 않게)
  useEffect(() => () => applyToDocument(DEFAULT_ZOOM), []);

  const setStep = useCallback(
    (next: number) => {
      setZoom(next);
      applyToDocument(next);
      if (!userId) return;
      writeCache(userId, next);
      if (savedRef.current === next) return;
      savedRef.current = next;
      saveUiPref(PREF_KEY, { zoom: next }).catch(() => {
        /* 저장 실패해도 이번 화면에는 이미 적용됨 — 다음 조작 때 다시 시도된다 */
      });
    },
    [userId],
  );

  const index = ZOOM_STEPS.indexOf(zoom as (typeof ZOOM_STEPS)[number]);
  const curIndex = index >= 0 ? index : ZOOM_STEPS.indexOf(DEFAULT_ZOOM);

  const value = useMemo<UiZoomContextValue>(
    () => ({
      zoom,
      zoomPercent: Math.round(zoom * 100),
      zoomIn: () => {
        if (curIndex < ZOOM_STEPS.length - 1) setStep(ZOOM_STEPS[curIndex + 1]);
      },
      zoomOut: () => {
        if (curIndex > 0) setStep(ZOOM_STEPS[curIndex - 1]);
      },
      resetZoom: () => setStep(DEFAULT_ZOOM),
      canZoomIn: curIndex < ZOOM_STEPS.length - 1,
      canZoomOut: curIndex > 0,
    }),
    [zoom, curIndex, setStep],
  );

  return <UiZoomContext.Provider value={value}>{children}</UiZoomContext.Provider>;
}

export function useUiZoom() {
  return useContext(UiZoomContext);
}
